import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, getDocs, doc, getDoc, addDoc, updateDoc } from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import "./Playlist.css";

const Playlist = () => {
  const { id: playlistId } = useParams();
  const [playlistName, setPlaylistName] = useState("");
  const [description, setDescription] = useState("");
  const [transition, setTransition] = useState("fade");
  const [playlistItems, setPlaylistItems] = useState([]);
  const [contentLibrary, setContentLibrary] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // ✅ Fetch content library from Firestore
  useEffect(() => {
    const fetchContentLibrary = async () => {
      try {
        const contentRef = collection(db, "media");
        const querySnapshot = await getDocs(contentRef);
        const content = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setContentLibrary(content);
      } catch (err) {
        console.error("❌ Error fetching content library:", err.message);
      }
    };

    fetchContentLibrary();
  }, []);

  // ✅ Fetch playlist data for editing
  useEffect(() => {
    const fetchPlaylistData = async () => {
      if (playlistId) {
        try {
          const playlistRef = doc(db, "playlists", playlistId);
          const playlistSnap = await getDoc(playlistRef);

          if (playlistSnap.exists()) {
            const playlistData = playlistSnap.data();
            setPlaylistName(playlistData.name || "");
            setDescription(playlistData.description || "");
            setTransition(playlistData.transition || "fade");
            setPlaylistItems(playlistData.items || []);
          } else {
            console.error("❌ Playlist not found.");
          }
        } catch (err) {
          console.error("❌ Error fetching playlist data:", err.message);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchPlaylistData();
  }, [playlistId]);

  // ✅ Prevent duplicate items in playlist
  const addToPlaylist = (content) => {
    if (playlistItems.some((item) => item.id === content.id)) {
      alert("⚠️ This content is already in the playlist.");
      return;
    }

    setPlaylistItems((prevItems) => [
      ...prevItems,
      { 
        ...content, 
        duration: content.duration || 10,
        url: content.url || "",  // ✅ Ensure `url` is included
        mediaType: content.mediaType || "unknown" // ✅ Ensure `mediaType` is always present
      },
    ]);
  };

  // ✅ Confirm before removing an item
  const removeFromPlaylist = (id) => {
    if (!window.confirm("❗ Are you sure you want to remove this item?")) return;

    setPlaylistItems((prevItems) => prevItems.filter((item) => item.id !== id));
  };

  // ✅ Update media duration
  const updateDuration = (id, newDuration) => {
    setPlaylistItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, duration: parseInt(newDuration) } : item
      )
    );
  };

  // ✅ Save Playlist with Full Media Information
  const handleSavePlaylist = async () => {
    if (!playlistName) {
      alert("⚠️ Please provide a playlist name.");
      return;
    }

    try {
      const playlistData = {
        name: playlistName,
        description,
        transition,
        items: playlistItems.map((item) => ({
          id: item.id,
          name: item.name,
          mediaType: item.mediaType,
          duration: item.duration,
          url: item.url || "", // ✅ Ensure `url` is included
          thumbnail: item.thumbnail || item.url || "/default-thumbnail.jpg",
        })),
        lastModified: new Date(),
      };

      if (playlistId) {
        const playlistRef = doc(db, "playlists", playlistId);
        const playlistSnap = await getDoc(playlistRef);

        if (playlistSnap.exists()) {
          await updateDoc(playlistRef, playlistData);
          alert("✅ Playlist updated successfully!");
        } else {
          alert("⚠️ Playlist does not exist. Creating a new one.");
          await addDoc(collection(db, "playlists"), playlistData);
        }
      } else {
        await addDoc(collection(db, "playlists"), playlistData);
        alert("✅ Playlist created successfully!");
      }

      navigate("/playlists");
    } catch (err) {
      console.error("❌ Error saving playlist:", err.message);
      alert("⚠️ Failed to save playlist. Please try again.");
    }
  };

  if (loading) return <p>Loading...</p>;

  const filteredContent = contentLibrary.filter((content) =>
    content.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalDuration = playlistItems.reduce(
    (total, item) => total + (item.duration || 0),
    0
  );

  // ✅ Truncate name to 7 characters
  const truncateName = (name, length = 7) => {
    return name.length > length ? name.substring(0, length) + "..." : name;
  };

  return (
    <div className="playlist-container">
      <h1 className="playlist-title">
        {playlistId ? "Edit Playlist" : "Create Playlist"}
      </h1>

      <div className="playlist-top-section">
        <div className="playlist-form">
          <input
            type="text"
            className="small-input"
            placeholder="Playlist Name"
            value={playlistName}
            onChange={(e) => setPlaylistName(e.target.value)}
          />
          <textarea
            className="small-input"
            placeholder="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>

      <div className="playlist-items-section">
        <h2>Playlist Items</h2>
        <p className="total-duration">Total Duration: {totalDuration} seconds</p>
        <div className="playlist-items-box">
          {playlistItems.map((item) => (
            <div className="playlist-item" key={item.id}>
              <div className="thumbnail small">
                <img src={item.thumbnail || item.url || "/default-thumbnail.jpg"} alt={item.name} />
              </div>
              <span className="content-name">{truncateName(item.name)}</span>
              <input
                type="number"
                className="duration-input"
                value={item.duration}
                onChange={(e) => updateDuration(item.id, e.target.value)}
              />
              <button className="remove-btn" onClick={() => removeFromPlaylist(item.id)}>
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="content-library">
        <h2>Content Library</h2>
        <input
          type="text"
          className="search-bar"
          placeholder="Search content..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="content-grid">
          {filteredContent.map((content) => (
            <div key={content.id} className="content-card" onClick={() => addToPlaylist(content)}>
              <div className="thumbnail">
                <img src={content.thumbnail || content.url || "/default-thumbnail.jpg"} alt={content.name} />
              </div>
              <p className="content-name">{truncateName(content.name)}</p>
            </div>
          ))}
        </div>
      </div>

      <button onClick={handleSavePlaylist} className="btn save-playlist-btn">
        Save Playlist
      </button>
    </div>
  );
};

export default Playlist;
